import { faCircleCheck, faPen, faTrashXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiCheckbox from "components/MuiCheckbox"
import MuiTextField from "components/MuiTextField"
import { Product } from "pages/Common/commonTypes"
import { IProjectCharger } from "pages/Projects/ProjectServices"
import { useState } from "react"

export interface ChargerTableRow {
    id: string
    sku_code: string
    display_name: string
    cin: string
    ocpp_id: string
    remarks: string
    project_id?: number
    product_id?: number
    project_linked_product_id?: number
    plp_usage_id?: number | null
    selected?: boolean
    status_label?: string | null
    sub_status_label?: string | null
    status_color?: string | null
    status_text_color?: string | null
}

interface ChargerTableProps {
    chargers: ChargerTableRow[]
    isStatiqSupplied: boolean
    products: Product[]
    onChargerChange: (index: number, field: keyof ChargerTableRow, value: string) => void
    onAddCharger: () => void
    onDeleteCharger: (index: number) => void
    onSkuChange: (index: number, product: Product | null) => void
    onToggleCharger: (index: number) => void
    showStatusColumns?: boolean
}

type EditingCell = {
    rowIndex: number
    field: keyof ChargerTableRow
} | null

const tableHeaderCellStyles = {
    color: "#70758D",
    fontSize: "11px",
    padding: "4px 8px",
    border: "1px solid #E5E7EB",
    fontWeight: 600,
    backgroundColor: "#FAFAFA",
}

const tableCellStyles = {
    color: "#101941",
    fontSize: "12px",
    padding: "0",
    border: "1px solid #E5E7EB",
    height: "36px",
}

interface EditableCellProps {
    value: string
    originalValue: string
    placeholder: string
    isEnabled: boolean
    isEditing: boolean
    onStartEdit: () => void
    onSave: () => void
    onDiscard: () => void
    onChange: (value: string) => void
}

const EditableCell = ({
    value,
    originalValue,
    placeholder,
    isEnabled,
    isEditing,
    onStartEdit,
    onSave,
    onDiscard,
    onChange,
}: EditableCellProps) => {
    if (isEditing) {
        return (
            <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center" }}>
                <MuiTextField
                    value={value}
                    placeholder={placeholder}
                    size="small"
                    autoFocus
                    onChange={e => onChange(e.target.value)}
                    sx={{
                        flex: 1,
                        height: "100%",
                        "& .MuiInputBase-root": {
                            height: "100%",
                            borderRadius: 0,
                        },
                        "& .MuiInputBase-input": {
                            padding: "4px 8px",
                        },
                    }}
                />
                <IconButton
                    size="small"
                    onClick={onSave}
                    sx={{
                        width: "24px",
                        height: "24px",
                        borderRadius: 0,
                        bgcolor: "#10B98114",
                        "&:hover": { bgcolor: "#10B98128" },
                    }}>
                    <FontAwesomeIcon icon={faCircleCheck} color="#10B981" size="xs" />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={onDiscard}
                    sx={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        alignItems: "center",
                        display: "flex",
                        bgcolor: "#F940541A",
                        "&:hover": { bgcolor: "#F9405433" },
                    }}>
                    <FontAwesomeIcon icon={faTrashXmark} color="#F94054" size="sm" />
                </IconButton>
            </Box>
        )
    }

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
                padding: "4px 8px",
                height: "100%",
                cursor: isEnabled ? "pointer" : "default",
                opacity: isEnabled ? 1 : 0.7,
            }}
            onClick={() => {
                if (isEnabled) {
                    onStartEdit()
                }
            }}>
            <Typography fontSize="12px" sx={{ flex: 1 }}>
                {originalValue || "-"}
            </Typography>
            {isEnabled && <FontAwesomeIcon icon={faPen} color="#70758D" size="xs" style={{ marginLeft: "4px" }} />}
        </Box>
    )
}

const ChargerTable = ({
    chargers,
    isStatiqSupplied,
    products,
    onChargerChange,
    onAddCharger,
    onDeleteCharger,
    onSkuChange,
    onToggleCharger,
    showStatusColumns = false,
}: ChargerTableProps) => {
    const [editingCell, setEditingCell] = useState<EditingCell>(null)
    const [editingValue, setEditingValue] = useState<string>("")

    const skuOptions = products.map(p => ({
        id: p.id,
        title: p.sku_code,
        description: p.display_name,
    }))

    const isEditingCell = (rowIndex: number, field: keyof ChargerTableRow) =>
        editingCell?.rowIndex === rowIndex && editingCell?.field === field

    const startEditing = (rowIndex: number, field: keyof ChargerTableRow, currentValue: string) => {
        setEditingCell({ rowIndex, field })
        setEditingValue(currentValue)
    }

    const saveEdit = () => {
        if (editingCell) {
            onChargerChange(editingCell.rowIndex, editingCell.field, editingValue)
        }
        setEditingCell(null)
        setEditingValue("")
    }

    const discardEdit = () => {
        setEditingCell(null)
        setEditingValue("")
    }

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <TableContainer
                className="custom-scrollbar"
                sx={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    overflowX: "auto",
                }}>
                <Table stickyHeader size="small" sx={{ minWidth: "1200px" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 50 }}>S.No</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 180 }}>SKU Code</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, minWidth: 200 }}>Display Name</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, minWidth: 100 }}>Project ID</TableCell>
                            {showStatusColumns && (
                                <>
                                    <TableCell sx={{ ...tableHeaderCellStyles, minWidth: 120 }}>Status</TableCell>
                                    <TableCell sx={{ ...tableHeaderCellStyles, minWidth: 120 }}>Sub Status</TableCell>
                                </>
                            )}
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 150 }}>CIN Number</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 250 }}>OCPP ID</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, minWidth: 150 }}>Remarks</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 60 }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {chargers.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={showStatusColumns ? 9 : 7}
                                    sx={{ ...tableCellStyles, textAlign: "center", color: "#70758D", padding: "12px" }}>
                                    No chargers added
                                </TableCell>
                            </TableRow>
                        ) : (
                            chargers.map((charger, index) => (
                                <TableRow key={charger.id}>
                                    <TableCell sx={{ ...tableCellStyles, padding: "4px 8px" }}>{index + 1}</TableCell>
                                    <TableCell sx={tableCellStyles}>
                                        {isStatiqSupplied ? (
                                            <Box sx={{ padding: "4px 8px" }}>
                                                <Typography fontSize="12px">{charger.sku_code || "-"}</Typography>
                                            </Box>
                                        ) : (
                                            <MuiAutoComplete
                                                disablePortal={false}
                                                options={skuOptions}
                                                value={
                                                    charger.sku_code
                                                        ? skuOptions.find(o => o.title === charger.sku_code) || null
                                                        : null
                                                }
                                                placeholder="Select SKU"
                                                size="small"
                                                containerHeight="100%"
                                                onChange={value => {
                                                    const selectedProduct = products.find(
                                                        p => p.id === (value as any)?.id
                                                    )
                                                    onSkuChange(index, selectedProduct || null)
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={tableCellStyles}>
                                        <Box sx={{ padding: "4px 8px" }}>
                                            <Typography fontSize="12px">{charger.display_name || "-"}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={tableCellStyles}>
                                        <Box sx={{ padding: "4px 8px" }}>
                                            <Typography fontSize="12px">{charger.project_id || "-"}</Typography>
                                        </Box>
                                    </TableCell>
                                    {showStatusColumns && (
                                        <>
                                            <TableCell sx={tableCellStyles}>
                                                <Box sx={{ padding: "4px 8px" }}>
                                                    <Typography fontSize="12px">
                                                        {charger.status_label || "-"}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={tableCellStyles}>
                                                <Box sx={{ padding: "4px 8px" }}>
                                                    {charger.sub_status_label ? (
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                display: "inline-block",
                                                                padding: "2px 8px",
                                                                borderRadius: "4px",
                                                                fontSize: "12px",
                                                                backgroundColor: charger.status_color || "#F3F4F6",
                                                                color: charger.status_text_color || "#101941",
                                                            }}>
                                                            {charger.sub_status_label}
                                                        </Box>
                                                    ) : (
                                                        <Typography fontSize="12px">-</Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell sx={tableCellStyles}>
                                        {isStatiqSupplied ? (
                                            <Box sx={{ padding: "4px 8px" }}>
                                                <Typography fontSize="12px">{charger.cin || "-"}</Typography>
                                            </Box>
                                        ) : (
                                            <MuiTextField
                                                value={charger.cin}
                                                placeholder="CIN"
                                                size="small"
                                                onChange={e => onChargerChange(index, "cin", e.target.value)}
                                                sx={{
                                                    height: "100%",
                                                    "& .MuiInputBase-root": { borderRadius: 0 },
                                                    "& .MuiInputBase-input": { padding: "4px 8px" },
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={tableCellStyles}>
                                        {isStatiqSupplied ? (
                                            <EditableCell
                                                isEnabled={!!charger.selected}
                                                value={isEditingCell(index, "ocpp_id") ? editingValue : charger.ocpp_id}
                                                originalValue={charger.ocpp_id}
                                                placeholder="Enter OCPP ID"
                                                isEditing={isEditingCell(index, "ocpp_id")}
                                                onStartEdit={() => startEditing(index, "ocpp_id", charger.ocpp_id)}
                                                onSave={saveEdit}
                                                onDiscard={discardEdit}
                                                onChange={setEditingValue}
                                            />
                                        ) : (
                                            <MuiTextField
                                                value={charger.ocpp_id}
                                                placeholder="OCPP ID"
                                                size="small"
                                                onChange={e => onChargerChange(index, "ocpp_id", e.target.value)}
                                                sx={{
                                                    height: "100%",
                                                    "& .MuiInputBase-root": { borderRadius: 0 },
                                                    "& .MuiInputBase-input": { padding: "4px 8px" },
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={tableCellStyles}>
                                        {isStatiqSupplied ? (
                                            <EditableCell
                                                isEnabled={!!charger.selected}
                                                value={isEditingCell(index, "remarks") ? editingValue : charger.remarks}
                                                originalValue={charger.remarks}
                                                placeholder="Enter Remarks"
                                                isEditing={isEditingCell(index, "remarks")}
                                                onStartEdit={() => startEditing(index, "remarks", charger.remarks)}
                                                onSave={saveEdit}
                                                onDiscard={discardEdit}
                                                onChange={setEditingValue}
                                            />
                                        ) : (
                                            <MuiTextField
                                                value={charger.remarks}
                                                placeholder="Remarks"
                                                size="small"
                                                onChange={e => onChargerChange(index, "remarks", e.target.value)}
                                                sx={{
                                                    height: "100%",
                                                    "& .MuiInputBase-root": { borderRadius: 0 },
                                                    "& .MuiInputBase-input": { padding: "4px 8px" },
                                                }}
                                            />
                                        )}
                                    </TableCell>

                                    <TableCell sx={{ ...tableCellStyles, padding: "4px 8px", textAlign: "center" }}>
                                        {isStatiqSupplied ? (
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                <MuiCheckbox
                                                    size="small"
                                                    checked={!!charger.selected}
                                                    onChange={() => {
                                                        if (editingCell?.rowIndex === index && charger.selected) {
                                                            discardEdit()
                                                        }
                                                        onToggleCharger(index)
                                                    }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onDeleteCharger(index)}
                                                    sx={{
                                                        width: "24px",
                                                        height: "24px",
                                                        bgcolor: "#F940541A",
                                                        "&:hover": { bgcolor: "#F9405433" },
                                                    }}>
                                                    <FontAwesomeIcon icon={faTrashXmark} color="#F94054" size="xs" />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <IconButton
                                                size="small"
                                                onClick={() => onDeleteCharger(index)}
                                                sx={{
                                                    width: "24px",
                                                    height: "24px",
                                                    bgcolor: "#F940541A",
                                                    "&:hover": {
                                                        bgcolor: "#F9405433",
                                                    },
                                                }}>
                                                <FontAwesomeIcon icon={faTrashXmark} color="#F94054" size="xs" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {!isStatiqSupplied && (
                <Box display="flex" justifyContent="center">
                    <Button variant="outlined" size="small" onClick={onAddCharger} sx={{ textTransform: "none" }}>
                        + Add Charger
                    </Button>
                </Box>
            )}
        </Box>
    )
}

export const mapProjectChargersToTableRows = (chargers: IProjectCharger[]): ChargerTableRow[] => {
    return chargers.map((charger, index) => ({
        id: `charger-${index}-${Date.now()}`,
        sku_code: charger.sku || "",
        display_name: charger.display_name || "",
        cin: charger.cin || "",
        ocpp_id: charger.ocpp_id || "",
        remarks: charger.remarks || "",
        project_linked_product_id: charger.project_linked_product_id,
        status_label: charger.operational_parent_status_label ?? null,
        sub_status_label: charger.operational_status_label ?? null,
        status_color: charger.operational_status_color ?? null,
        status_text_color: charger.operational_status_text_color ?? null,
    }))
}

export const createEmptyChargerRow = (): ChargerTableRow => ({
    id: `charger-new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    sku_code: "",
    display_name: "",
    cin: "",
    ocpp_id: "",
    remarks: "",
})

export default ChargerTable
